import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { debounce } from 'throttle-debounce';

import { actions as analyticsActions } from '../actions/analytics';
import { actions, routes } from '../actions/events';
import { actions as taraActions } from '../actions/tara';
import MainContent from '../components/layout/MainContent';
import EventHeader from '../components/features/Event/EventHeader';
import EventFooter from '../components/features/Event/EventFooter';
import FiltersPopover from '../components/features/FiltersPopover';
import Subtitle from '../components/typography/Subtitle';

import CuratedSeaticsMapWidgetContainer from '../containers/CuratedSeaticsMapWidgetContainer';
import DefaultSeaticsMapWidgetContainer from '../containers/DefaultSeaticsMapWidgetContainer';
import TaraSeaticsMapWidgetContainer from '../containers/TaraSeaticsMapWidgetContainer';
import TaraPopoverContainer from '../containers/TaraPopoverContainer';

import { createFetchingView } from '../helpers/views';
import { getAppIsReady } from '../reducers/app/isReady';
import { getEventsById } from '../reducers/events/byId';
import { getIsFetching } from '../reducers/events/isFetching';
import { getStatus } from '../reducers/events/status';
import { getTaraHasMadeIntroduction } from '../reducers/tara/hasMadeIntroduction';
import { getTaraHasCuratedTickets } from '../reducers/tara/hasCuratedTickets';

const LOADING = 'LOADING';
const READY = 'READY';
const NO_EVENT = 'NO_EVENT';
const NO_TICKETS = 'NO_TICKETS';

const parseLocationQuery = ({ priceLow, priceHigh, ...locationQuery }) => ({
  ...locationQuery,
  priceLow: priceLow ? parseInt(priceLow, 10) : 1,
  priceHigh: priceHigh ? parseInt(priceHigh, 10) : 5,
});

const filtersChanged = (filtersOne, filtersTwo) => {
  const filtersOneKeys = Object.keys(filtersOne);
  const filtersTwoKeys = Object.keys(filtersTwo);

  const lengthOne = filtersOneKeys.length;
  const lengthTwo = filtersTwoKeys.length;

  if (lengthOne !== lengthTwo) {
    return true;
  }

  for (let idx = 0; idx < lengthOne; idx += 1) {
    const valueOne = filtersOne[filtersOneKeys[idx]];
    const valueTwo = filtersTwo[filtersTwoKeys[idx]];

    if (valueOne !== valueTwo) {
      return true
    }
  }

  return false
};

const removeUndefinedAndEmptyStrings = (key, obj) => {
  switch (typeof obj[key]) {
    case 'string':
      return !obj[key].length && delete obj[key];
    case 'undefined':
      return delete obj[key];
    default:
      return null;
  }
};

const getBrowseAllParams = ({ curated, tara, persona, ...params }) => params;

class EventsShowView extends React.PureComponent {
  state = this.getInitialState(this.props);
  locationPrev = this.props.locationPrev;

  componentDidMount() {
    this.hasMounted = true;

    const { id } = this.props.locationPayload;
    const { curated, tara, ...filters } = this.props.locationQuery;

    this.props.apiEventsShowRequest(id);

    if ((curated || tara) && Object.keys(filters).length) {
      this.props.apiEventsTicketsCuratedRequest(id, filters);
      this.props.taraSetHasMadeIntroduction(true);
      this.props.taraSetHasCuratedTickets(true);
    } else if (!curated && !tara) {
      this.props.apiEventsTicketsRequest(id, getBrowseAllParams(filters));
    }

    window.addEventListener('resize', this.handleWindowResize);
  }

  componentWillReceiveProps(nextProps) {
    const {
      isFetching,
    } = nextProps;

    if (isFetching !== this.props.isFetching && isFetching) {
      this.setFetching(isFetching);
    }

    const {
      currentEvent,
      locationPrev,
      locationQuery,
    } = nextProps;

    const isChangingLocationPrev = locationPrev.type !== this.props.location.type;

    if (isChangingLocationPrev && locationPrev.type !== this.locationPrev.type) {
      this.locationPrev = locationPrev;
    }

    if (locationQuery.tara && currentEvent) {
      if (!this.state.shouldShowAllTickets && currentEvent && currentEvent.recommendedList && !currentEvent.recommendedList.length) {
        // TARA couldn't find any recommended tickets
        this.setShouldShowAllTickets(true);
        this.setHasNoRecommendedTickets(true);
      }
    }
  }

  componentWillUpdate(nextProps, nextState) {
    const {
      locationQuery,
    } = nextProps;

    const {
      shouldShowAllTickets,
    } = nextState;

    const hasChangedShouldShowAllTickets = shouldShowAllTickets !== this.state.shouldShowAllTickets;

    const {
      curated,
      tara,
      ...filters
    } = locationQuery;

    const hasChangedFilters = Object.keys(filters).length && filtersChanged(locationQuery, this.props.locationQuery);

    if (hasChangedFilters || hasChangedShouldShowAllTickets) {
      if ((tara || curated) && !shouldShowAllTickets) {
        this.props.apiEventsTicketsCuratedRequest(this.eventId, filters);
      } else if (!this.props.isFetching) {
        this.props.apiEventsTicketsRequest(this.eventId, getBrowseAllParams(filters));
      }
    }
  }

  componentWillUnmount() {
    this.hasMounted = false;

    window.removeEventListener('resize', this.handleWindowResize);

    this.props.taraSetHasCuratedTickets(false);
  }

  getInitialState(props) {
    const { locationQuery } = props;
    const { curated, tara, defaultSort } = locationQuery;

    const isOpenFilters = this.shouldInitiallyShowFilters;

    return {
      mapStatus: LOADING,
      isOpenFilters,
      currentFilters: curated || tara ? parseLocationQuery(props.locationQuery) : {},
      seaticsFilterOptions: {
        sortType: defaultSort,
      },
      eventHeaderHeight: 0,
      isViewingMap: false,
      shouldShowAllTickets: false,
      hasNoRecommendedTickets: false,
      viewedPopoverOnce: !isOpenFilters,
    };
  }

  getNextFilters(nextQuery) {
    const nextFilters = {
      ...this.props.locationQuery,
      ...nextQuery,
    };

    Object.keys(nextFilters).forEach((key) => removeUndefinedAndEmptyStrings(key, nextFilters));
    Object.keys(nextQuery).forEach((key) => removeUndefinedAndEmptyStrings(key, nextQuery));

    const shouldUpdate = filtersChanged(nextFilters, this.props.locationQuery);

    return shouldUpdate ? nextFilters : this.props.locationQuery;
  };

  get eventId() {
    return this.props.locationPayload.id.toString();
  }

  get shouldInitiallyShowFilters() {
    const { curated, tara, ...filters } = this.props.locationQuery;

    if (curated || tara) {
      return !Object.keys(filters).length;
    }

    return false;
  }

  setCurrentFilters = currentFilters => this.setState({ currentFilters, viewedPopoverOnce: true });

  setEventHeaderHeight = eventHeaderHeight => (this.hasMounted ? this.setState({ eventHeaderHeight }) : null);

  setEventHeaderRef = (ref) => {
    if (!this.eventHeaderRef && !this.state.eventHeaderHeight) {
      this.eventHeaderRef = ref;

      setTimeout(() => {
        this.setEventHeaderHeight(this.eventHeaderRef.clientHeight);
      }, 250);
    }
  };

  setFetching = fetching => {
    if (this.hasMounted) {
      this.props.setFetching(fetching);
    }
  };

  setIsOpenFilters = isOpenFilters => this.setState({ isOpenFilters });

  setIsViewingMap = isViewingMap => this.setState({ isViewingMap });

  setMapStatus = mapStatus => (this.hasMounted ? this.setState({ mapStatus }) : null);

  setShouldShowAllTickets = shouldShowAllTickets => this.setState({ shouldShowAllTickets });
  setHasNoRecommendedTickets = hasNoRecommendedTickets => this.setState({ hasNoRecommendedTickets });

  handleCancelFilters = () => {
    this.setIsOpenFilters(false);
    this.props.analyticsRecordActionRequest('event','cancelfilters', '');
  };


  handleClickBrowseAllTickets = (e, formState) => {
    this.updateFilters(formState, true);
    this.props.analyticsRecordActionRequest('event','browseall', '');
  };

  handleClickBuyButton = (ticket, quantity) => {
    this.props.analyticsBuyTicketRequest(ticket, quantity);
  };

  handleClickFilters = () => {
    this.setIsOpenFilters(true);
    this.props.analyticsRecordActionRequest('event','filters', '');
  };

  handleClickFindMyTickets = (e, formState) => {
    this.updateFilters(formState, false);
    this.props.analyticsRecordActionRequest('event','findtickets', '');
  };

  handleChangeSort = (item) => {
    this.setState({
      seaticsFilterOptions: {
        ...this.state.seaticsFilterOptions,
        sortType: item ? item.value : undefined,
      },
    });
    this.props.analyticsRecordActionRequest('event','sorttickets', '');
  };

  handleClickToggleMap = () => {
    this.setIsViewingMap(!this.state.isViewingMap);
    this.props.analyticsRecordActionRequest('event','togglemap', this.state.isViewingMap);
  };

  handleNoEvent = () => {
    this.setMapStatus(NO_EVENT);
    this.setFetching(false);
    this.props.analyticsRecordActionRequest('event','noevent', '');
  };

  handleNoTickets = () => {
    this.setMapStatus(NO_TICKETS);
    this.setFetching(false);
    this.props.analyticsRecordActionRequest('event','notickets', '');
  };

  handleSeaticsEvent = (type, data) => {
    switch (type) {
      case 'LoadEvent':
        if (data === 'FinishedDrawingMap') {
          this.props.analyticsRecordActionRequest('event','loaded', this.eventId);
          return this.handleSeaticsEventFinishedLoading();
        }
        return null;
      case 'MapInteraction':
        this.props.analyticsRecordActionRequest('event','mapevent', data);
        return null;
      case 'BuyButtonClicked':
      default:
        return null;
    }
  };

  handleSeaticsEventFinishedLoading = () => {
    this.setMapStatus(READY);
    this.setFetching(false);
  };

  handleSelectTicket = (ticket) => {
    this.selectedTicket = ticket;
    this.props.analyticsViewTicketRequest(ticket);
  };

  handleWindowResize = debounce(300, true, (e) => {
    if (this.eventHeaderRef) {
      setTimeout(() => {
        this.setEventHeaderHeight(this.eventHeaderRef.clientHeight);
      }, 250);
    }
  });

  updateFilters(nextFilters, shouldShowAllTickets) {
    const currentFilters = this.getNextFilters(nextFilters);

    this.updateTaraStatus();
    this.setIsOpenFilters(false);
    this.setCurrentFilters(currentFilters);
    this.setShouldShowAllTickets(shouldShowAllTickets);

    this.props.routeEventsShow(this.eventId, currentFilters);
  }

  updateTaraStatus() {
    if (!this.props.hasMadeIntroduction) {
      this.props.taraSetHasMadeIntroduction(true);
    }

    if (!this.props.taraHasCuratedTickets) {
      this.props.taraSetHasCuratedTickets(true);
    }
  };

  renderHeader() {
    const { currentEvent } = this.props;

    if (!currentEvent || !currentEvent.dateTime) {
      return <div style={{height: 96}} />;
    }

    return (
      <EventHeader
        {...currentEvent}
        innerRef={this.setEventHeaderRef}
        isViewingMap={this.state.isViewingMap}
      />
    );
  }

  renderNoEvent() {
    return <Subtitle>Event has expired.</Subtitle>;
  }

  renderNoTickets() {
    return <Subtitle>No tickets found.</Subtitle>;
  }

  renderFooter() {
    return (
      <EventFooter
        locationPrev={this.locationPrev}
      />
    );
  }

  renderPopover() {
    if (!this.props.appIsReady) {
      return null;
    }

    const { locationQuery } = this.props;
    let { curated, tara, ...queryProps} = locationQuery;

    if (tara) {
      return (
        <TaraPopoverContainer
          isOpen={this.state.isOpenFilters}
          onClickFindMyTickets={this.handleClickFindMyTickets}
          onClickBrowseAllTickets={this.handleClickBrowseAllTickets}
          onCancel={this.handleCancelFilters}
          isAnimated={!this.state.viewedPopoverOnce}
          canDismiss={this.state.viewedPopoverOnce}
          {...queryProps}
        />
      );
    }

    return (
      <FiltersPopover
        isOpen={this.state.isOpenFilters}
        onClickFindMyTickets={this.handleClickFindMyTickets}
        onClickBrowseAllTickets={this.handleClickBrowseAllTickets}
        onCancel={this.handleCancelFilters}
        canDismiss
        {...queryProps}
      />
    );
  }

  renderMap() {
    const {
      locationPayload,
      locationQuery,
    } = this.props;

    const id = locationPayload.id.toString();

    const { curated, tara } = locationQuery;

    const seaticsMapContainerProps = {
      currentFilters: this.state.currentFilters,
      eventId: id,
      eventHeaderHeight: this.state.eventHeaderHeight,
      isViewingMap: this.state.isViewingMap,
      mapStatus: this.state.mapStatus,
      onClickBuyButton: this.handleClickBuyButton,
      onClickFilters: this.handleClickFilters,
      onClickToggleMap: this.handleClickToggleMap,
      onChangeSort: this.handleChangeSort,
      onNoEvent: this.handleNoEvent,
      onNoTickets: this.handleNoTickets,
      onSeaticsEvent: this.handleSeaticsEvent,
      onSelectTicket: this.handleSelectTicket,
      seaticsFilterOptions: this.state.seaticsFilterOptions,
      status: this.props.status,
      isFetching: this.props.isFetching,
      shouldShowAllTickets: this.state.shouldShowAllTickets,
      hasNoRecommendedTickets: this.state.hasNoRecommendedTickets,
    };

    if (tara) {
      return (
        <TaraSeaticsMapWidgetContainer
          {...seaticsMapContainerProps}
        />
      );
    }

    if (curated) {
      return (
        <CuratedSeaticsMapWidgetContainer
          {...seaticsMapContainerProps}
        />
      );
    }

    return (
      <DefaultSeaticsMapWidgetContainer
        {...seaticsMapContainerProps}
      />
    )
  }

  render() {
    const {
      mapStatus,
    } = this.state;

    return (
      <MainContent
        id="EventsShowView"
        className="sk-fade-in-quarter-second"
        hasNoPaddingUnderSm
      >
        {this.renderHeader()}

        {mapStatus === NO_EVENT && this.renderNoEvent()}
        {mapStatus === NO_TICKETS && this.renderNoTickets()}

        {this.renderMap()}

        {(mapStatus === NO_EVENT || mapStatus === NO_TICKETS) && this.renderFooter()}

        {this.renderPopover()}
      </MainContent>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  appIsReady: getAppIsReady(state),
  currentEvent: getEventsById(state)[ownProps.locationPayload.id],
  isFetching: getIsFetching(state),
  status: getStatus(state),
  hasMadeIntroduction: getTaraHasMadeIntroduction(state),
  taraHasCuratedTickets: getTaraHasCuratedTickets(state),
  ...ownProps,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  apiEventsShowRequest: actions.apiEventsShowRequest,
  apiEventsTicketsRequest: actions.apiEventsTicketsRequest,
  apiEventsTicketsCuratedRequest: actions.apiEventsTicketsCuratedRequest,
  routeEventsShow: routes.routeEventsShow,
  ...analyticsActions,
  ...taraActions,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(createFetchingView(EventsShowView));



// WEBPACK FOOTER //
// ./src/views/EventsShowView.js